/**
 * Logistics import.
 *
 * Points at the existing import screen rather than duplicating it. The
 * deliveries dataset, the file-hash duplicate check, the preview and the
 * rollback all live there already, and a second uploader would be a second
 * place for the same bug to live.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui';
import { SectionTitle } from './parts';

export function LogisticsImportPage(): ReactNode {
  return (
    <div className="space-y-4">
      <Card>
        <SectionTitle>Importing delivery history</SectionTitle>
        <p className="text-sm text-slate-600">
          Historical deliveries are brought in through the existing import screen, with{' '}
          <span className="font-medium text-slate-900">Delivery history</span> as the dataset.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          A preview is written before anything is committed, the same file cannot be uploaded twice,
          and a committed batch can be rolled back. Imported deliveries never create a sale, an
          expense or a rider payout — the money was recognised when the ride happened, and
          recognising it again would raise a debt that was already settled.
        </p>
        <Link
          to="/imports"
          className="mt-3 inline-flex rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          Open import
        </Link>
      </Card>

      <Card>
        <SectionTitle>What the importer asks for</SectionTitle>
        <ul className="space-y-1.5 text-sm text-slate-600">
          <li>• A reference per delivery, which is what makes a re-import a no-op.</li>
          <li>• The date, kept exactly as the sheet has it, in this business&rsquo;s timezone.</li>
          <li>• The fee, and the rider&rsquo;s earnings where the sheet states them.</li>
          <li>
            • A Rider ID where the sheet has one — it decides who rode, and two spellings of one
            name resolve to one rider without anybody approving a guess.
          </li>
          <li>• The source, so an App delivery is costed at its own rate and not the Shop&rsquo;s.</li>
        </ul>
      </Card>
    </div>
  );
}
